// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// 🧹 REMOVÍVEL · A ENTRADA NA FORMAÇÃO A PARTIR DO MAPA
// ============================================================================
//
// 📗 2026-10-28 · pedido do cliente: entrar na página «Formação» (`/formacao`) a partir do mapa,
//    de forma simples e removível — um botão na barra de cima e um comando no `Ctrl+K`.
//
// ⚠️ QUANDO APARECE: só com a formação ligada (`formacao_activa` = 1) e a quem participa ou gere
//    a lista (superadmin ou proprietário de um plano). Quem decide é o SERVIDOR, no campo
//    `entradaNaFormacao` de `GET /api/auth/sessao` — a leitura que o mapa já faz uma vez ao abrir.
//    Nenhuma chamada nova, e nenhuma por clique. Se essa leitura falhar, o campo não chega e o
//    botão não aparece — sem erro no ecrã (o `.catch` silencioso do `Mapa.tsx`, que já lá estava).
//
// ⚠️ Este ficheiro NÃO importa nada de `formacao/`: o guarda do `Formacao.test.tsx` recusa que o
//    mapa importe a página. Liga-se a ela por endereço, num separador novo — o mapa fica aberto.
//
// ⚠️ Sem emoji (o pedido dizia «🎓 Formação»): o mapa usa ícones SVG de cor única, um `<path>`,
//    e há uma prova que recusa emoji (`Mapa.test.tsx`). O chapéu de formatura vai em SVG.
//
// ---------------------------------------------------------------------------
// COMO RETIRAR (no fim da formação)
// ---------------------------------------------------------------------------
//   1. Desligar já: `formacao_activa` ≠ 1 em Configuração — o botão e o comando desaparecem.
//   2. Retirar do código:
//      · apagar este ficheiro e a sua prova (`entrada-na-formacao.test.tsx`);
//      · no `Mapa.tsx`, apagar as linhas marcadas «🧹 REMOVÍVEL · formação» (o import, o estado
//        `formacaoNoMapa`, a leitura do campo, o comando na paleta e o `<BotaoDaFormacao>`);
//      · no servidor, o campo `entradaNaFormacao` de `GET /api/auth/sessao` e a função
//        `entradaNaFormacao` de `apps/api/src/formacao.ts`.
// ============================================================================

import type { ComandoDaPaleta } from './PaletaDeComandos.js';

/** O endereço da página «Formação» (o ramo de `App.tsx`). */
export const ENDERECO_DA_FORMACAO = '/formacao';

/** O que o `title` do botão diz — e o detalhe do comando na paleta. */
const PARA_QUE_SERVE = 'a página da formação: as ações a testar, as marcas e os guias (abre noutro separador)';

/** Abre a página «Formação» num separador novo (o mapa fica onde estava). */
export function abrirFormacao(): void {
  window.open(ENDERECO_DA_FORMACAO, '_blank', 'noopener');
}

/**
 * O comando da paleta (`Ctrl+K`) — só se acrescenta quando o servidor disse que sim.
 * Escreve-se «formação» ou «formacao»: as duas grafias que alguém escreve na procura.
 */
export const COMANDO_DA_FORMACAO: ComandoDaPaleta = {
  chave: 'formacao formação guia treino',
  rotulo: 'Formação',
  detalhe: PARA_QUE_SERVE,
  fazer: abrirFormacao,
};

/**
 * O botão da barra de cima. `visivel = false` não desenha nada.
 *
 * Uma ligação e não um botão com `onClick`: abre com o rato do meio, copia-se o endereço, e o
 * navegador trata do separador novo.
 */
export function BotaoDaFormacao({ visivel }: { readonly visivel: boolean }): React.ReactElement | null {
  if (!visivel) return null;
  return (
    <a
      className="btn btn--peq btn--formacao"
      href={ENDERECO_DA_FORMACAO}
      target="_blank"
      rel="noopener"
      title={`Formação — ${PARA_QUE_SERVE}`}
      aria-label="Formação"
    >
      <svg
        viewBox="0 0 16 16"
        width="1em"
        height="1em"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {/* Chapéu de formatura — um só `<path>`, como os ícones de `icones.tsx`. */}
        <path d="M1.5 6 8 3l6.5 3L8 9zM4.5 7.4v3.1c0 .9 1.6 1.9 3.5 1.9s3.5-1 3.5-1.9V7.4M14.5 6v3.5" />
      </svg>
      {/* 📗 2026-10-29 · o rótulo cede quando a fila do topo aperta (`.rotulo-cede`, `mapa.css`). */}
      <span className="rotulo-cede">Formação</span>
    </a>
  );
}
